import { useCallback, useEffect, useRef, useState } from "react";

interface AudioRecorderState {
    isRecording: boolean;
    duration: number;
    error: string | null;
}

export const useAudioRecorder = () => {
    const [state, setState] = useState<AudioRecorderState>({
        isRecording: false,
        duration: 0,
        error: null,
    });

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const resolveRef = useRef<((file: File) => void) | null>(null);

    const cleanup = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        mediaRecorderRef.current = null;
        chunksRef.current = [];
    }, []);

    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    const startRecording = useCallback(async (): Promise<File> => {
        try {
            setState({ isRecording: true, duration: 0, error: null });
            chunksRef.current = [];

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            const filePromise = new Promise<File>((resolve) => {
                resolveRef.current = resolve;

                mediaRecorder.onstop = () => {
                    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                    const file = new File([blob], `recording-${Date.now()}.webm`, {
                        type: "audio/webm",
                    });
                    resolve(file);
                };
            });

            mediaRecorder.start();

            intervalRef.current = setInterval(() => {
                setState((prev) => ({ ...prev, duration: prev.duration + 1 }));
            }, 1000);

            return filePromise;
        } catch (err) {
            cleanup();
            const message = err instanceof Error ? err.message : "Failed to access microphone";
            setState({ isRecording: false, duration: 0, error: message });
            throw err;
        }
    }, [cleanup]);

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
        }
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        setState((prev) => ({ ...prev, isRecording: false }));
    }, []);

    const cancelRecording = useCallback(() => {
        cleanup();
        resolveRef.current = null;
        setState({ isRecording: false, duration: 0, error: null });
    }, [cleanup]);

    return {
        isRecording: state.isRecording,
        duration: state.duration,
        error: state.error,
        startRecording,
        stopRecording,
        cancelRecording,
    };
};
